import React, { useCallback, useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { api } from '../api';

const formatNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number.toLocaleString('en-IN') : '—';
};

const formatMoney = (value) => {
  if (!Number.isFinite(value)) return '—';

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 6,
    maximumFractionDigits: 6,
  }).format(value);
};

// Rates are supplied by the backend pricing cache, in USD per 1 million tokens.
const estimateRowCost = (row, prices) => {
  const million = 1_000_000;
  const pricing = prices?.[row.model];

  if (!pricing) return null;

  if (row.model === 'gpt-image-2') {
    const imagePricing = pricing.modalities?.image;
    const textPricing = pricing.modalities?.text;

    // The API must provide image token counts before image costs can be estimated.
    if (
      row.inputImageTokens === null ||
      row.outputImageTokens === null ||
      !imagePricing ||
      !textPricing ||
      !Number.isFinite(imagePricing.input) ||
      !Number.isFinite(imagePricing.output) ||
      !Number.isFinite(textPricing.input) ||
      !Number.isFinite(textPricing.cachedInput)
    ) {
      return null;
    }

    const textInputTokens = Math.max(
      0,
      row.inputTokens - row.inputImageTokens
    );
    const cachedTextTokens = Math.min(
      textInputTokens,
      row.cachedInputTokens
    );
    const uncachedTextTokens = Math.max(
      0,
      textInputTokens - cachedTextTokens
    );

    let cost =
      uncachedTextTokens * textPricing.input +
      cachedTextTokens * textPricing.cachedInput +
      row.inputImageTokens * imagePricing.input +
      row.outputImageTokens * imagePricing.output;

    // If non-image output tokens are present, include them only when
    // the pricing data provides an applicable text output rate.
    const outputTextTokens = Math.max(
      0,
      row.outputTokens - row.outputImageTokens
    );

    if (outputTextTokens > 0) {
      if (!Number.isFinite(textPricing.output)) return null;
      cost += outputTextTokens * textPricing.output;
    }

    return cost / million;
  }

  if (
    !Number.isFinite(pricing.input) ||
    !Number.isFinite(pricing.output)
  ) {
    return null;
  }

  const cachedRate = Number.isFinite(pricing.cachedInput)
    ? pricing.cachedInput
    : null;

  const cached = Math.min(row.inputTokens, row.cachedInputTokens);
  const uncached = Math.max(0, row.inputTokens - cached);

  // Do not guess a cached-input rate if the official page does not publish one.
  if (cached > 0 && cachedRate === null) return null;

  return (
    uncached * pricing.input +
    cached * (cachedRate ?? pricing.input) +
    row.outputTokens * pricing.output
  ) / million;
};

const sumCompletionMetrics = (buckets) => {
  const totals = {
    requests: 0,
    inputTokens: 0,
    outputTokens: 0,
    cachedInputTokens: 0,
    outputTextTokens: 0,
    outputImageTokens: 0,
  };

  for (const bucket of buckets || []) {
    for (const result of bucket.results || []) {
      totals.requests += Number(result.num_model_requests) || 0;
      totals.inputTokens += Number(result.input_tokens) || 0;
      totals.outputTokens += Number(result.output_tokens) || 0;
      totals.cachedInputTokens += Number(result.input_cached_tokens) || 0;
      totals.outputTextTokens += Number(result.output_text_tokens) || 0;
      totals.outputImageTokens += Number(result.output_image_tokens) || 0;
    }
  }

  return totals;
};

function MetricCard({ label, value, note }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
      <p className="text-sm font-medium text-gray-500">{label}</p>
      <p className="mt-2 break-words text-2xl font-bold text-gray-900">{value}</p>
      {note && <p className="mt-1 text-xs text-gray-500">{note}</p>}
    </div>
  );
}

export default function OpenAICostAnalysis() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadOverview = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const response = await api.getOpenAIUsageOverview();
      setReport(response);

      if (!response || (!response.success && !response.partial)) {
        setError(response?.error || 'Unable to load the OpenAI overview.');
      }
    } catch (err) {
      setError(err?.message || 'Unable to load the OpenAI overview.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  const completions = report?.completions;
  const pricingRates = report?.pricing?.prices || {};
  const usageTotals = sumCompletionMetrics(completions?.data);
  const istDateTime = (timestamp) => {
    if (!timestamp) return { date: '—', time: '—' };

    const date = new Date(timestamp * 1000);
    return {
      date: new Intl.DateTimeFormat('en-IN', {
        timeZone: 'Asia/Kolkata',
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      }).format(date),
      time: new Intl.DateTimeFormat('en-IN', {
        timeZone: 'Asia/Kolkata',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      }).format(date),
    };
  };

  const usageRows = (completions?.data || []).flatMap((bucket) =>
    (bucket.results || []).map((result) => {
      const timestamp = Number(bucket.start_time) || 0;
      const ist = istDateTime(timestamp);
      const dateObject = new Date(timestamp * 1000);
      const dateKey = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Asia/Kolkata',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).format(dateObject);

      const inputImageTokens = result.input_image_tokens == null
        ? null
        : Number(result.input_image_tokens) || 0;

      const outputImageTokens = result.output_image_tokens == null
        ? null
        : Number(result.output_image_tokens) || 0;

      const row = {
        date: ist.date,
        dateKey,
        timestamp,
        time: ist.time,
        model: result.model || 'Unknown model',
        requests: Number(result.num_model_requests) || 0,
        inputTokens: Number(result.input_tokens) || 0,
        outputTokens: Number(result.output_tokens) || 0,
        cachedInputTokens: Number(result.input_cached_tokens) || 0,
        inputImageTokens,
        outputImageTokens,
      };

      row.estimatedCost = estimateRowCost(row, pricingRates);
      return row;
    })
  );

  // Group by IST calendar day, newest day first; newest hour first within each day.
  const rowsByDay = new Map();
  for (const row of usageRows) {
    if (!rowsByDay.has(row.dateKey)) rowsByDay.set(row.dateKey, []);
    rowsByDay.get(row.dateKey).push(row);
  }

  const tableRows = [];
  const dayKeys = [...rowsByDay.keys()].sort((a, b) => b.localeCompare(a));

  for (const dayKey of dayKeys) {
    const rows = rowsByDay.get(dayKey).sort(
      (a, b) => b.timestamp - a.timestamp || a.model.localeCompare(b.model)
    );

    tableRows.push(...rows);

    const knownCosts = rows
      .map((row) => row.estimatedCost)
      .filter((cost) => cost !== null);

    const hasUnknownCosts = knownCosts.length !== rows.length;

    tableRows.push({
      type: 'subtotal',
      key: `subtotal-${dayKey}`,
      date: rows[0]?.date || dayKey,
      subtotal: knownCosts.reduce((sum, cost) => sum + cost, 0),
      subtotalLabel: hasUnknownCosts
        ? 'Daily subtotal (known models only)'
        : 'Daily subtotal',
    });

    tableRows.push({
      type: 'spacer',
      key: `spacer-${dayKey}`,
    });
  }

  // No blank separator after the final (oldest) day.
  if (tableRows.length && tableRows[tableRows.length - 1].type === 'spacer') {
    tableRows.pop();
  }

  const errors = report?.errors || [];

  return (
    <Layout>
      <div className="mx-auto max-w-7xl space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              OpenAI Cost &amp; Usage Analysis
            </h1>
            <p className="mt-1 text-sm text-gray-600">
              Essential report for the last three completed UTC days.
            </p>
            {report?.period && (
              <p className="mt-1 text-xs text-gray-500">
                {report.period.start_date} to {report.period.end_date_exclusive}
                {' '}UTC (end date exclusive)
              </p>
            )}
          </div>

          <button
            type="button"
            onClick={loadOverview}
            disabled={loading}
            className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {loading ? 'Loading…' : 'Refresh overview'}
          </button>
        </div>

        {loading && !report && (
          <div className="rounded-xl border border-gray-200 bg-white p-6 text-sm text-gray-600">
            Loading the essential report…
          </div>
        )}

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
            {error}
          </div>
        )}

        {report?.partial && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
            Some report requests failed. Totals below include only successfully
            retrieved data; they may be incomplete.
          </div>
        )}

        {report && (
          <>
            <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
              <MetricCard
                label="Requests made to OpenAI"
                value={formatNumber(report.summary?.requests_made)}
                note="Two sequential requests per overview load"
              />
              <MetricCard
                label="Model requests"
                value={completions?.success ? formatNumber(usageTotals.requests) : 'Unavailable'}
                note="Completion usage endpoint"
              />
              <MetricCard
                label="Input tokens"
                value={completions?.success ? formatNumber(usageTotals.inputTokens) : 'Unavailable'}
              />
              <MetricCard
                label="Output tokens"
                value={completions?.success ? formatNumber(usageTotals.outputTokens) : 'Unavailable'}
              />
            </section>

            <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">
                    Hourly usage by model (IST)
                  </h2>
                  <p className="mt-1 text-sm text-gray-500">
                    Each row represents an hourly bucket converted from UTC to India Standard Time.
                    Costs are estimates from published token rates, shown to six decimal places.
                  </p>
                </div>
              </div>

              {report?.pricing?.metadata?.warning && (
                <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                  Pricing refresh warning: {report.pricing.metadata.warning}
                  {report.pricing.metadata.stale
                    ? ' Cached rates may be outdated.'
                    : ''}
                </div>
              )}

              {!completions?.success ? (
                <p className="mt-4 text-sm text-gray-600">
                  Completion usage data is unavailable.
                </p>
              ) : usageRows.length === 0 ? (
                <p className="mt-4 text-sm text-gray-600">
                  No completion usage rows were returned for this period.
                </p>
              ) : (
                <div className="mt-4 overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                      <tr>
                        <th className="px-3 py-3">Date (IST)</th>
                        <th className="px-3 py-3">Time (IST)</th>
                        <th className="px-3 py-3">Model</th>
                        <th className="px-3 py-3 text-right">Requests</th>
                        <th className="px-3 py-3 text-right">Input tokens</th>
                        <th className="px-3 py-3 text-right">Output tokens</th>
                        <th className="px-3 py-3 text-right">Cached input</th>
                        <th className="px-3 py-3 text-right">Output image tokens</th>
                        <th className="whitespace-nowrap px-3 py-3 text-right">Estimated cost (USD)</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {tableRows.map((row, index) => {
                        if (row.type === 'spacer') {
                          return (
                            <tr key={row.key} aria-hidden="true">
                              <td colSpan={9} className="h-5 border-0 bg-gray-50 p-0" />
                            </tr>
                          );
                        }

                        if (row.type === 'subtotal') {
                          return (
                            <tr key={row.key} className="bg-gray-100 font-semibold text-gray-900">
                              <td colSpan={8} className="px-3 py-3 text-right">
                                {row.subtotalLabel} — {row.date}
                              </td>
                              <td className="whitespace-nowrap px-3 py-3 text-right">
                                {formatMoney(row.subtotal)}
                              </td>
                            </tr>
                          );
                        }

                        return (
                          <tr key={`${row.dateKey}-${row.timestamp}-${row.model}-${index}`}>
                            <td className="whitespace-nowrap px-3 py-3">{row.date}</td>
                            <td className="whitespace-nowrap px-3 py-3">{row.time}</td>
                            <td className="whitespace-nowrap px-3 py-3">{row.model}</td>
                            <td className="px-3 py-3 text-right">{formatNumber(row.requests)}</td>
                            <td className="px-3 py-3 text-right">{formatNumber(row.inputTokens)}</td>
                            <td className="px-3 py-3 text-right">{formatNumber(row.outputTokens)}</td>
                            <td className="px-3 py-3 text-right">{formatNumber(row.cachedInputTokens)}</td>
                            <td className="px-3 py-3 text-right">
                              {row.outputImageTokens === null ? '—' : formatNumber(row.outputImageTokens)}
                            </td>
                            <td className="whitespace-nowrap px-3 py-3 text-right">
                              {row.estimatedCost === null ? '—' : formatMoney(row.estimatedCost)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {completions?.truncated && (
                <p className="mt-3 text-sm text-amber-800">
                  OpenAI reports additional pages. This overview intentionally did
                  not fetch them; deeper pagination can be added as a separate action.
                </p>
              )}

            </section>

            {errors.length > 0 && (
              <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
                <h2 className="text-lg font-semibold text-gray-900">Request issues</h2>
                <div className="mt-3 space-y-3">
                  {errors.map((item, index) => (
                    <div
                      key={`${item.report || 'request'}-${index}`}
                      className="rounded-lg border border-gray-200 p-3 text-sm"
                    >
                      <p className="font-semibold text-gray-800">
                        {item.report || 'Request'}
                        {item.http_status ? ` — HTTP ${item.http_status}` : ''}
                      </p>
                      <p className="mt-1 break-words text-gray-600">{item.error}</p>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}
